import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import { hasPermission } from '../../lib/permissions'
import {
  targetMarginDollars,
  compareToGuide,
  meetsGuide,
  suggestedBuyPrice,
  GUIDE_VERDICT_LABEL,
  money0,
} from '../../lib/marginGuide'
import { sourcingViewUrl } from '../../lib/autograbSourcing'
import CreateDealForm from '../forms/CreateDealForm'
import PhotoLightbox from '../PhotoLightbox'
import { ppsrService } from '../../services/ppsrService'
import './SourcedVehicles.css'

type SortKey =
  | 'year' | 'make' | 'model' | 'kms' | 'buy' | 'sell'
  | 'margin_dollar' | 'margin_pct' | 'target' | 'guide' | 'ppsr' | 'seller' | 'age'

interface SourcedVehiclesProps {
  user: User
  /** A row handed over from the vehicle search panel above. */
  prefill?: any
  onPrefillConsumed?: () => void
}

export default function SourcedVehicles({ user, prefill, onPrefillConsumed }: SourcedVehiclesProps) {
  const [vehicles, setVehicles] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [rowPrefill, setRowPrefill] = useState<any>(null)
  const formRef = useRef<HTMLDivElement | null>(null)
  /* The sourcing screen and the pipeline are two views of the same table, so a listing
     that has become a deal opens that deal rather than leaving anyone to go and find it. */
  const navigate = useNavigate()
  // Winnie's review: the source filter replaces the PPSR one — All Vehicles /
  // Private source / Dealer source, driven off seller_type.
  const [sourceFilter, setSourceFilter] = useState<'all' | 'private' | 'dealer'>('all')
  // Winnie's review: the default list surfaces vehicles that meet the margin
  // pricing guide.
  const [guideFilter, setGuideFilter] = useState<'guide' | 'all'>('guide')
  // The list opens on the biggest margin in dollars. A percentage sort puts a
  // cheap car with a thin dollar margin above an expensive one carrying real
  // money, which is the wrong thing to look at first thing in the morning.
  const [sortKey, setSortKey] = useState<SortKey>('margin_dollar')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [searchTerm, setSearchTerm] = useState('')

  /* A car taken off this screen is not deleted — the deal stays in the pipeline and
     stays in the history the margin guide is measured against. This only hides it
     from the list of cars still being considered, and it can be put back from the
     same screen, which is why there is no confirmation step. */
  const [showRemoved, setShowRemoved] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [ppsrBusyId, setPpsrBusyId] = useState<string | null>(null)

  /* Winnie, 11 Sep: once "+ Deal" has turned a listing into a deal, the listing must not
     stay selectable for a second one. The row is not deleted - it is the audit trail back
     to the AutoGrab feed entry, and the deal may yet be cancelled - it is filtered out of
     the default view, and this toggle brings the pile back. */
  const [showConverted, setShowConverted] = useState(false)
  /* What became of each of those deals, so "Return to sourcing list" is a decision made
     with the facts in front of you rather than a guess. */
  const [convertedDeals, setConvertedDeals] = useState<Record<string, { status: string; gone: boolean }>>({})
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxImages, setLightboxImages] = useState<string[]>([])
  const [lightboxSource, setLightboxSource] = useState<'marketplace' | 'stock' | null>(null)

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const { data, error } = await supabase
          .from('deals')
          .select('*')
          .is('deleted_at', null)
          // Only the cars we found on Autograb and keyed in by hand. Without this the
          // screen listed every deal in the business, which is Stock Management's job
          // and made this one a duplicate of it. Autograb's Sourcing API is not enabled
          // on the account, so a lookup by VIN or rego is the only way a car gets here,
          // and that lookup is what stamps autograb_vehicle_id.
          .not('autograb_vehicle_id', 'is', null)
          .order('created_at', { ascending: false })

        if (error) throw error
        setVehicles(data || [])

        const convertedIds = (data || [])
          .map((v: Deal) => v.sourcing_converted_deal_id)
          .filter((id): id is string => Boolean(id))
        if (convertedIds.length) {
          const { data: made } = await supabase
            .from('deals')
            .select('id, deal_status, deleted_at')
            .in('id', convertedIds)
          const seen: Record<string, { status: string; gone: boolean }> = {}
          // A listing pointing at a deal that no longer exists is worth saying out loud.
          for (const id of convertedIds) seen[id] = { status: '', gone: true }
          for (const m of (made || []) as any[]) {
            seen[m.id] = { status: String(m.deal_status || ''), gone: Boolean(m.deleted_at) }
          }
          setConvertedDeals(seen)
        } else {
          setConvertedDeals({})
        }
        setLoadError(null)
      } catch (err) {
        // Was console.error only, so a failed query rendered as
        // "No vehicles match your filters" — a broken screen that looks empty.
        console.error('Error fetching sourced vehicles:', err)
        setLoadError(err instanceof Error ? err.message : 'Could not load sourced vehicles')
      } finally {
        setLoading(false)
      }
    }

    setLoading(true)
    fetchVehicles()

    const subscription = supabase
      .channel('sourced_vehicles_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'deals' },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setVehicles((prev) => prev.filter((v) => v.id !== payload.old.id))
          } else {
            const next = payload.new as Deal
            /* Only cars that came off an AutoGrab lookup belong on this screen. Without this
               the deal created from a listing arrived here as a row of its own - the duplicate
               Winnie saw - because every insert into deals came through this channel. */
            if (!next.autograb_vehicle_id) {
              setVehicles((prev) => prev.filter((v) => v.id !== next.id))
              return
            }
            if (next.deleted_at) {
              setVehicles((prev) => prev.filter((v) => v.id !== next.id))
            } else {
              setVehicles((prev) => [next, ...prev.filter((v) => v.id !== next.id)])
            }
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [reloadKey])

  const openFormWith = (v: any) => {
    setRowPrefill(v)
    setShowForm(true)
    // The form renders above the table, so on row 30 it opened off-screen and
    // looked like nothing had happened.
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  useEffect(() => {
    if (prefill) {
      openFormWith(prefill)
      onPrefillConsumed?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill])

  // Fall back across looked-up (autograb_*) and manual (vehicle_*) fields.
  const dYear = (v: Deal) => v.autograb_year ?? v.vehicle_year
  const dMake = (v: Deal) => v.autograb_make || v.vehicle_make || ''
  const dModel = (v: Deal) => v.autograb_model || v.vehicle_model || ''
  const dKms = (v: Deal) => v.autograb_odometer ?? v.vehicle_odometer
  const dSell = (v: Deal) => v.autograb_market_value ?? v.estimated_sale_price

  const dBuy = (v: Deal) => Number(v.valuation_amount ?? 0)
  const dMarginDollars = (v: Deal) => (v.gross_margin === null || v.gross_margin === undefined ? null : Number(v.gross_margin))
  // The band is read off the market value, not what we paid — Winnie's ruling
  // on 24 August. Falls back to 0 when there is no market value, which lands in
  // the lowest band, exactly as banding on a missing buy price used to.
  const dGuideBasis = (v: Deal) => Number(dSell(v) ?? 0)
  const dRemoved = (v: Deal) => Boolean(v.sourcing_dismissed_at)
  const dConverted = (v: Deal) => Boolean(v.sourcing_converted_deal_id)

  /* What happened to the deal this listing produced. Read from the map loaded above; a
     listing whose deal has been deleted says so, because that is the case where putting
     it back on the list is the right thing to do. */
  const convertedNote = (v: Deal): string => {
    const made = v.sourcing_converted_deal_id ? convertedDeals[v.sourcing_converted_deal_id] : null
    if (!made) return 'A deal has been created from this listing.'
    if (made.gone) return 'The deal created from this listing no longer exists.'
    if (!made.status) return 'A deal has been created from this listing.'
    return 'Deal created, now at ' + made.status.replace(/_/g, ' ') + '.'
  }

  /* Winnie, 11 Sep: cancelling a deal does not quietly put its listing back on the list.
     Somebody decides to, here, knowing what became of the deal. The deal itself is not
     touched - this only clears the stamp on the listing. */
  const returnToSourcing = async (vehicle: Deal) => {
    setBusyId(vehicle.id)
    const patch = {
      sourcing_converted_deal_id: null,
      sourcing_converted_at: null,
      sourcing_converted_by: null,
    }
    const { error } = await supabase.from('deals').update(patch).eq('id', vehicle.id)
    if (error) {
      console.error('Could not return the listing to the sourcing list', error)
      setLoadError(error.message)
    } else {
      setVehicles((prev) => prev.map((v) => (v.id === vehicle.id ? { ...v, ...patch } : v)))
      setLoadError(null)
    }
    setBusyId(null)
  }

  /* Flag on the row, not a delete. Optimistic, because the alternative is a table
     that sits still for a moment after a click and invites a second one. */
  const setRemoved = async (vehicle: Deal, removed: boolean) => {
    setBusyId(vehicle.id)
    const patch = {
      sourcing_dismissed_at: removed ? new Date().toISOString() : null,
      sourcing_dismissed_by: removed ? user.id : null,
    }

    const { error } = await supabase.from('deals').update(patch).eq('id', vehicle.id)
    if (error) {
      console.error('Could not change the sourcing list', error)
      setLoadError(error.message)
    } else {
      setVehicles((prev) =>
        prev.map((v) => (v.id === vehicle.id ? { ...v, ...patch } : v)),
      )
      setLoadError(null)
    }
    setBusyId(null)
  }
  /* Run, or re-run, the PPSR search for one car. The Function writes the answer onto
     the deal itself and the realtime subscription above brings it back to this table,
     so there is nothing to refetch here. Optimistic, because a row that sits still
     after a click invites a second one - and a second search costs money. */
  const runPpsr = async (vehicle: Deal) => {
    const vin = (vehicle.vehicle_vin || '').trim()
    const rego = (vehicle.registration_number || '').trim()
    const state = (vehicle.registration_state || '').trim()
    if (!vin && !(rego && state)) return

    setPpsrBusyId(vehicle.id)
    setVehicles((prev) =>
      prev.map((v) => (v.id === vehicle.id ? { ...v, ppsr_status: 'pending' as const } : v)),
    )
    const res = await ppsrService.runSearch(vehicle.id, { vin, rego, state })
    if (!res.success) {
      // On the row, not in a toast that has gone by the time anyone looks up.
      setVehicles((prev) =>
        prev.map((v) => (v.id === vehicle.id ? { ...v, ppsr_status: 'error' as const } : v)),
      )
    }
    setPpsrBusyId(null)
  }


  const dTarget = (v: Deal) => targetMarginDollars(dGuideBasis(v))
  // What the guide would have allowed us to pay for this car. Solved from the
  // market value rather than subtracted from it — the band depends on the buy
  // price, so the two only agree while both sit inside the same band.
  const dSuggested = (v: Deal) => suggestedBuyPrice(dSell(v))
  const dVerdict = (v: Deal) => compareToGuide(dGuideBasis(v), dMarginDollars(v))

  const removedCount = vehicles.filter(dRemoved).length
  const convertedCount = vehicles.filter(dConverted).length

  let filtered = vehicles.filter((v) => {
    // Removed cars are a separate pile, not mixed into the live list.
    if (dRemoved(v) !== showRemoved) return false

      /* A listing that has already become a deal is out of the default view, so nobody
         can create a second deal from it by accident. Hidden, never deleted. */
      if (dConverted(v) && !showConverted) return false

    // Private source covers individuals and private companies; dealer source is
    // the LMCT / auction / trade counterparties.
    if (sourceFilter === 'private' && !['individual', 'company'].includes(v.seller_type || 'individual')) return false
    if (sourceFilter === 'dealer' && v.seller_type !== 'dealer') return false
    if (guideFilter === 'guide' && !meetsGuide(dGuideBasis(v), dMarginDollars(v))) return false
    if (searchTerm) {
      const hay = `${dYear(v) || ''} ${dMake(v)} ${dModel(v)} ${v.seller_name || ''} ${v.vehicle_vin || v.autograb_vin || ''}`.toLowerCase()
      if (!hay.includes(searchTerm.trim().toLowerCase())) return false
    }
    return true
  })

  // Every column heading sorts, both ways (Winnie's review).
  const sortValue = (v: Deal, key: SortKey): string | number => {
    switch (key) {
      case 'year': return dYear(v) || 0
      case 'make': return dMake(v).toLowerCase()
      case 'model': return dModel(v).toLowerCase()
      case 'kms': return dKms(v) ?? 0
      case 'buy': return dBuy(v)
      case 'sell': return dSell(v) ?? 0
      case 'margin_dollar': return dMarginDollars(v) ?? Number.NEGATIVE_INFINITY
      case 'margin_pct': return v.margin_percent ?? Number.NEGATIVE_INFINITY
      case 'target': return dTarget(v)
      case 'guide': return GUIDE_VERDICT_LABEL[dVerdict(v)]
      case 'ppsr': return v.ppsr_status || ''
      case 'seller': return (v.seller_name || '').toLowerCase()
      case 'age': return new Date(v.created_at || 0).getTime()
      default: return 0
    }
  }

  filtered = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1
    const va = sortValue(a, sortKey)
    const vb = sortValue(b, sortKey)
    if (va < vb) return -1 * dir
    if (va > vb) return 1 * dir
    return 0
  })

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(key)
      // Text sorts read better A→Z; numbers read better biggest-first.
      setSortDir(['make', 'model', 'seller', 'ppsr', 'guide'].includes(key) ? 'asc' : 'desc')
    }
  }

  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '')

  const handlePhotoClick = (vehicle: Deal) => {
    const images: string[] = []
    if (vehicle.photo_url) images.push(vehicle.photo_url)
    if (Array.isArray(vehicle.additional_photos)) images.push(...vehicle.additional_photos)
    if (images.length > 0) {
      setLightboxImages(images)
      setLightboxSource(vehicle.photo_source || null)
      setLightboxOpen(true)
    }
  }

  const getMarginColor = (marginPercent?: number): string => {
    if (marginPercent == null) return 'gray'
    if (marginPercent >= 20) return 'green'
    if (marginPercent >= 10) return 'yellow'
    return 'red'
  }

/* What we could search this car on. A VIN is enough on its own; a plate only counts
   with the state that issued it, because that is what the register needs. */
const ppsrIdentifier = (v: Deal): string => {
  const vin = (v.vehicle_vin || '').trim()
  if (vin) return vin
  const rego = (v.registration_number || '').trim()
  const state = (v.registration_state || '').trim()
  return rego && state ? rego : ''
}

/* Retry is only offered where it could do something: the search failed, or none has
   been run, and we hold an identifier to run it with. */
const ppsrRetryable = (v: Deal): boolean =>
  Boolean(ppsrIdentifier(v)) && (v.ppsr_status === 'error' || !v.ppsr_status)

/* PPSR on a row, told honestly. Two of these states are new: a car can be mid-search,
   and a car can carry neither a VIN nor a rego - in which case nobody can search it,
   and 'Not checked' quietly blames the staff for something they cannot do. */
const getPpsrBadge = (v: Deal) => {
  switch (v.ppsr_status) {
    case 'clear':
      return <span className="ppsr-badge ppsr-clear">Clear</span>
    case 'encumbered':
      return <span className="ppsr-badge ppsr-encumbered">Encumbered</span>
    case 'stolen':
      return <span className="ppsr-badge ppsr-stolen">Stolen</span>
    case 'written_off':
      return <span className="ppsr-badge ppsr-stolen">Written off</span>
    case 'pending':
      return <span className="ppsr-badge ppsr-checking">Checking</span>
    case 'unavailable':
      return <span className="ppsr-badge ppsr-none">No VIN or rego</span>
    case 'error':
      return <span className="ppsr-badge ppsr-error">Failed</span>
    default:
      if (!ppsrIdentifier(v)) {
        return <span className="ppsr-badge ppsr-none">No VIN or rego</span>
      }
      return <span className="ppsr-badge ppsr-none">Not checked</span>
  }
}

  const money = (n?: number) => (n != null ? `$${Math.round(n).toLocaleString()}` : '—')

  const getAgeDisplay = (createdAt?: string): string => {
    if (!createdAt) return '—'
    const hours = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)
    if (days > 0) return `${days}d`
    if (hours > 0) return `${hours}h`
    return '<1h'
  }

  return (
    <div className="view sourced-vehicles-view">
      <div className="view-header">
        <div className="view-title-section">
          <h1>Autograb Sourcing</h1>
          <p>Cars found on Autograb and entered by hand — not the whole yard</p>
        </div>
        <div className="view-header-actions">
          {/* Opens the team's saved AutoGrab view — newest first, retail only,
              with the damaged / write-off / auction / no-odometer tags excluded.
              The parameters are built from the same constants the feed filter
              uses, so the two cannot drift apart. */}
          <a
            className="ghost-button"
            href={sourcingViewUrl()}
            target="_blank"
            rel="noopener noreferrer"
            title="Newest first, retail only, excluding new, damaged, write-off, auction, mismatch and missing-odometer listings"
          >
            Open in AutoGrab ↗
          </a>
          {hasPermission(user, 'perm-create-deal') && (
            <button
              className="primary-button"
              onClick={() => {
                setRowPrefill(null)
                setShowForm(!showForm)
              }}
            >
              {showForm ? 'Cancel' : '+ Create Deal'}
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="load-error">
          <span>{loadError}</span>
          <button className="action-btn" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
        </div>
      )}

      {showForm && (
        <div className="card form-card" ref={formRef}>
          <CreateDealForm
            userId={user.id}
            user={user}
            prefill={rowPrefill}
            onSuccess={() => {
              /* The listing this came from has just been stamped as used. Reload so the table
                 and the created-deal statuses behind "Return to sourcing list" are both current. */
              setReloadKey((k) => k + 1)
              setShowForm(false)
              setRowPrefill(null)
            }}
          />
        </div>
      )}

      {/*
        The margin band table is deliberately not shown here. It is Direct
        Auto's own buying rule, and this screen is looked at with sellers and
        visitors in the room. The guide still drives everything below it — the
        Guide target column, the vs Guide verdict and the "meets guide" filter
        all come from lib/marginGuide — it just is not printed on the wall.
      */}

      <div className="sourced-controls">
        <div className="sourced-filters">
          <select className="filter-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as any)}>
            <option value="all">All Vehicles</option>
            <option value="private">Private source</option>
            <option value="dealer">Dealer source</option>
          </select>

          <select className="filter-select" value={guideFilter} onChange={(e) => setGuideFilter(e.target.value as any)}>
            <option value="guide">Meets margin guide</option>
            <option value="all">All Margins</option>
          </select>

          <input
            type="text"
            className="filter-search"
            placeholder="Search make/model/year..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

            {/* Winnie, 11 Sep: off by default. The listings that have already produced a
                deal are still here for anyone who needs the full history. */}
            {convertedCount > 0 && (
              <button
                type="button"
                className={showConverted ? 'filter-toggle is-on' : 'filter-toggle'}
                onClick={() => setShowConverted((s) => !s)}
                title="Listings that have already been turned into a deal are hidden so the same car cannot be bought twice. This brings them back into view."
              >
                Show deals already created ({convertedCount})
              </button>
            )}

            {/* Mirrors the pipeline's "Show closed & cancelled" — the removed cars are
                a pile you can open, not a thing that vanished. */}
            {(removedCount > 0 || showRemoved) && (
              <button
                type="button"
                className={showRemoved ? 'filter-toggle is-on' : 'filter-toggle'}
                onClick={() => setShowRemoved((s) => !s)}
              >
                {showRemoved ? 'Back to sourcing' : `Removed (${removedCount})`}
              </button>
            )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading sourced vehicles...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🚗</div>
          {vehicles.length === 0 ? (
            <>
              <p>Nothing sourced from Autograb yet</p>
              <p className="empty-hint">
                Look a car up by VIN or rego and it lands here. Everything already
                bought lives in Stock Management.
              </p>
              {hasPermission(user, 'perm-create-deal') && (
                <button className="primary-button" onClick={() => openFormWith(null)}>+ Create the first deal</button>
              )}
            </>
          ) : (
            <>
              <p>
                None of the {vehicles.length} sourced vehicles match your filters
                {guideFilter === 'guide' ? ' — the list is showing only vehicles that meet the margin pricing guide' : ''}
              </p>
              <button
                className="action-btn"
                onClick={() => {
                  setSourceFilter('all')
                  setGuideFilter('all')
                  setSearchTerm('')
                  setSortKey('margin_pct')
                  setSortDir('desc')
                }}
              >
                Clear filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="sourced-table-wrapper">
          <table className="sourced-table">
            <thead>
              <tr>
                <th className="col-photo">Photo</th>
                <th className="col-year sortable" onClick={() => toggleSort('year')}>Year{arrow('year')}</th>
                <th className="col-make sortable" onClick={() => toggleSort('make')}>Make{arrow('make')}</th>
                <th className="col-model sortable" onClick={() => toggleSort('model')}>Model{arrow('model')}</th>
                <th className="col-kms sortable" onClick={() => toggleSort('kms')}>Kms{arrow('kms')}</th>
                <th className="col-buy sortable" onClick={() => toggleSort('buy')}>Buy{arrow('buy')}</th>
                <th className="col-sell sortable" onClick={() => toggleSort('sell')}>Sell{arrow('sell')}</th>
                <th className="col-margin-dollar sortable" onClick={() => toggleSort('margin_dollar')}>Margin ${arrow('margin_dollar')}</th>
                <th className="col-margin-percent sortable" onClick={() => toggleSort('margin_pct')}>Margin %{arrow('margin_pct')}</th>
                <th className="col-target sortable" onClick={() => toggleSort('target')} title="Target margin from the pricing guide for this price band">Guide target{arrow('target')}</th>
                <th className="col-suggested" title="The most the guide allows us to pay at this market value">Suggested</th>
                <th className="col-guide sortable" onClick={() => toggleSort('guide')}>vs Guide{arrow('guide')}</th>
                <th className="col-ppsr sortable" onClick={() => toggleSort('ppsr')}>PPSR{arrow('ppsr')}</th>
                <th className="col-seller sortable" onClick={() => toggleSort('seller')}>Seller{arrow('seller')}</th>
                <th className="col-age sortable" onClick={() => toggleSort('age')}>Age{arrow('age')}</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((vehicle) => (
                <tr key={vehicle.id} className={`sourced-row ${dRemoved(vehicle) ? 'is-removed' : ''} margin-${getMarginColor(vehicle.margin_percent)}`}>
                  <td className="col-photo">
                    {vehicle.photo_url ? (
                      <button className="photo-thumb" onClick={() => handlePhotoClick(vehicle)} title="View photos">
                        <img src={vehicle.photo_url} alt="Vehicle" />
                        {vehicle.additional_photos && vehicle.additional_photos.length > 0 && (
                          <span className="photo-count">📷 {vehicle.additional_photos.length + 1}</span>
                        )}
                      </button>
                    ) : (
                      <div className="photo-placeholder" title="No photo">📷</div>
                    )}
                  </td>
                  <td className="col-year">{dYear(vehicle) || '—'}</td>
                  <td className="col-make">{dMake(vehicle) || '—'}</td>
                  <td className="col-model">{dModel(vehicle) || '—'}</td>
                  <td className="col-kms">{dKms(vehicle) != null ? dKms(vehicle)!.toLocaleString() : '—'}</td>
                  <td className="col-buy">{money(vehicle.valuation_amount)}</td>
                  <td className="col-sell">{money(dSell(vehicle))}</td>
                  <td className="col-margin-dollar">{money(vehicle.gross_margin)}</td>
                  <td className="col-margin-percent">
                    <strong>{vehicle.margin_percent != null ? `${vehicle.margin_percent.toFixed(1)}%` : '—'}</strong>
                  </td>
                  <td className="col-target">{money0(dTarget(vehicle))}</td>
                  <td className="col-suggested">
                    {dSuggested(vehicle) === null ? '—' : money0(dSuggested(vehicle) as number)}
                  </td>
                  <td className="col-guide">
                    <span className={`guide-badge guide-${dVerdict(vehicle)}`}>
                      {GUIDE_VERDICT_LABEL[dVerdict(vehicle)]}
                    </span>
                  </td>
                  <td className="col-ppsr">
                    {getPpsrBadge(vehicle)}
                    {ppsrRetryable(vehicle) && (
                      <button
                        className="action-btn action-btn-quiet ppsr-retry"
                        onClick={(e) => {
                          e.stopPropagation()
                          runPpsr(vehicle)
                        }}
                        disabled={ppsrBusyId === vehicle.id}
                        title="Run the PPSR search for this car"
                      >
                        Retry
                      </button>
                    )}
                  </td>
                  <td className="col-seller">{vehicle.seller_name}</td>
                  <td className="col-age">{getAgeDisplay(vehicle.created_at)}</td>
                  <td className="col-actions">
                    {hasPermission(user, 'perm-create-deal') && (
                      <button
                        className="action-btn"
                        title={
                          dConverted(vehicle)
                            ? 'A deal has already been created from this listing. Return it to the sourcing list first if it really needs to be used again.'
                            : 'Start a new deal pre-filled from this vehicle'
                        }
                        disabled={dConverted(vehicle)}
                        onClick={() =>
                          openFormWith({
                            make: dMake(vehicle),
                            model: dModel(vehicle),
                            year: dYear(vehicle),
                            vin: vehicle.autograb_vin || vehicle.vehicle_vin,
                      // Carried into the form so the row can be stamped once the deal exists.
                      sourcing_listing_id: vehicle.id,
                            odometer_km: dKms(vehicle),
                            price: vehicle.valuation_amount,
                            seller_name: vehicle.seller_name,
                          })
                        }
                      >
                        + Deal
                      </button>
                    )}
                    {dConverted(vehicle) && (
                      <button
                        type="button"
                        className="sourced-converted-note"
                        title={convertedNote(vehicle) + ' Opens the deal.'}
                                              disabled={
                          !vehicle.sourcing_converted_deal_id ||
                          Boolean((convertedDeals[vehicle.sourcing_converted_deal_id] || {}).gone)
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          if (vehicle.sourcing_converted_deal_id) {
                            navigate('/deals/' + vehicle.sourcing_converted_deal_id)
                          }
                        }}
                      >
                        {convertedNote(vehicle)}
                      </button>
                    )}

                    {dConverted(vehicle) && hasPermission(user, 'perm-create-deal') && (
                      <button
                        className="action-btn action-btn-quiet"
                        title="Put this listing back on the sourcing list so a deal can be created from it again. The deal already created is not touched."
                        disabled={busyId === vehicle.id}
                        onClick={() => returnToSourcing(vehicle)}
                      >
                        Return to sourcing list
                      </button>
                    )}

                    {hasPermission(user, 'perm-create-deal') &&
                      (dRemoved(vehicle) ? (
                        <button
                          className="action-btn action-btn-quiet"
                          title="Put this car back on the sourcing list"
                          disabled={busyId === vehicle.id}
                          onClick={() => setRemoved(vehicle, false)}
                        >
                          Put back
                        </button>
                      ) : (
                        <button
                          className="action-btn action-btn-quiet"
                          title="Take this car off the sourcing list. The deal stays in the pipeline."
                          disabled={busyId === vehicle.id}
                          onClick={() => setRemoved(vehicle, true)}
                        >
                          Remove
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lightboxOpen && (
        <PhotoLightbox images={lightboxImages} source={lightboxSource} onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  )
}
